import React, { useEffect, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ZapIcon, FlameIcon, DropletIcon, WifiIcon, SmartphoneIcon, LandmarkIcon, ChevronRightIcon, ArrowLeftIcon, PlusIcon, FileTextIcon, ClockIcon, CheckCircle2Icon, XIcon, ShareIcon, CopyIcon, HomeIcon, CreditCardIcon, GraduationCapIcon, BuildingIcon, LoaderIcon, FingerprintIcon, ScanFaceIcon, LockIcon, RefreshCwIcon, QrCodeIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 12
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.35,
      ease: 'easeOut'
    }
  }
};
const stagger = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06
    }
  }
};
type Step = 'home' | 'view_all_payees' | 'biller_list' | 'enter_consumer' | 'bill_details' | 'review' | 'auth' | 'processing' | 'success';
type AuthMethod = 'mpin' | 'thumb' | 'face';
interface SavedPayee {
  id: string;
  company: string;
  consumerName: string;
  consumerNumber: string;
  amount: number;
  daysLeft: number;
  category: string;
  iconBg: string;
  icon: React.ElementType;
  email: string;
}
interface Biller {
  id: string;
  name: string;
  email: string;
  category: string;
  iconBg: string;
}
const CATEGORIES = [{
  id: 'electricity',
  icon: ZapIcon,
  label: 'Electricity'
}, {
  id: 'gas',
  icon: FlameIcon,
  label: 'Gas'
}, {
  id: 'water',
  icon: DropletIcon,
  label: 'Water'
}, {
  id: 'internet',
  icon: WifiIcon,
  label: 'Internet'
}, {
  id: '1bill',
  icon: FileTextIcon,
  label: '1Bill'
}, {
  id: 'government',
  icon: LandmarkIcon,
  label: 'Government'
}, {
  id: 'education',
  icon: GraduationCapIcon,
  label: 'Education'
}, {
  id: 'credit',
  icon: CreditCardIcon,
  label: 'Credit Card'
}, {
  id: 'rental',
  icon: HomeIcon,
  label: 'Rental'
}];
const ALL_BILLERS: Biller[] = [{
  id: '1bill',
  name: '1BILL Invoice/Voucher',
  email: 'onebillinvoice@bopay',
  category: '1bill',
  iconBg: '#FBBF24'
}, {
  id: 'lesco',
  name: 'LESCO',
  email: 'lesco@bopay',
  category: 'electricity',
  iconBg: '#F37024'
}, {
  id: 'kelectric',
  name: 'K-Electric',
  email: 'kelectric@bopay',
  category: 'electricity',
  iconBg: '#0EA5E9'
}, {
  id: 'iesco',
  name: 'IESCO',
  email: 'iesco@bopay',
  category: 'electricity',
  iconBg: '#22C55E'
}, {
  id: 'sngpl',
  name: 'SNGPL',
  email: 'sngpl@bopay',
  category: 'gas',
  iconBg: '#EF4444'
}, {
  id: 'ssgc',
  name: 'SSGC',
  email: 'ssgc@bopay',
  category: 'gas',
  iconBg: '#F97316'
}, {
  id: 'kwsb',
  name: 'Karachi Water Board',
  email: 'kwsb@bopay',
  category: 'water',
  iconBg: '#0EA5E9'
}, {
  id: 'wasa',
  name: 'WASA Lahore',
  email: 'wasa@bopay',
  category: 'water',
  iconBg: '#06B6D4'
}, {
  id: 'stormfiber',
  name: 'StormFiber',
  email: 'stormfiber@bopay',
  category: 'internet',
  iconBg: '#1E3A8A'
}, {
  id: 'ptcl',
  name: 'PTCL',
  email: 'ptcl@bopay',
  category: 'internet',
  iconBg: '#0891B2'
}, {
  id: 'nayatel',
  name: 'Nayatel',
  email: 'nayatel@bopay',
  category: 'internet',
  iconBg: '#6366F1'
}, {
  id: 'jazz',
  name: 'Jazz',
  email: 'jazz@bopay',
  category: 'mobile',
  iconBg: '#DC2626'
}, {
  id: 'zong',
  name: 'Zong',
  email: 'zong@bopay',
  category: 'mobile',
  iconBg: '#16A34A'
}, {
  id: 'ufone',
  name: 'Ufone',
  email: 'ufone@bopay',
  category: 'mobile',
  iconBg: '#FBBF24'
}, {
  id: 'telenor',
  name: 'Telenor',
  email: 'telenor@bopay',
  category: 'mobile',
  iconBg: '#0EA5E9'
}, {
  id: 'fbr',
  name: 'FBR Income Tax',
  email: 'fbr@bopay',
  category: 'government',
  iconBg: '#15803D'
}, {
  id: 'punjab_excise',
  name: 'Punjab Excise & Tax',
  email: 'pet@bopay',
  category: 'government',
  iconBg: '#7C3AED'
}, {
  id: 'aiou',
  name: 'Allama Iqbal Open University',
  email: 'aiou@bopay',
  category: 'education',
  iconBg: '#1E40AF'
}, {
  id: 'lums',
  name: 'LUMS',
  email: 'lums@bopay',
  category: 'education',
  iconBg: '#9F1239'
}, {
  id: 'bop_cc',
  name: 'BOP Credit Card',
  email: 'creditcards@bopay',
  category: 'credit',
  iconBg: '#F37024'
}, {
  id: 'hbl_cc',
  name: 'HBL Credit Card',
  email: 'cards@hbl',
  category: 'credit',
  iconBg: '#008266'
}, {
  id: 'ubl_cc',
  name: 'UBL Credit Card',
  email: 'cards@ubl',
  category: 'credit',
  iconBg: '#1E40AF'
}];
const INITIAL_PAYEES: SavedPayee[] = [{
  id: 'p1',
  company: 'StormFiber',
  consumerName: 'Muhammad Sohail Shawani',
  consumerNumber: '03223556978',
  amount: 3448,
  daysLeft: 10,
  category: 'internet',
  iconBg: '#1E3A8A',
  icon: WifiIcon,
  email: 'stormfiber@bopay'
}, {
  id: 'p2',
  company: 'LESCO',
  consumerName: 'Ali Hassan',
  consumerNumber: '034982349823',
  amount: 12450,
  daysLeft: 7,
  category: 'electricity',
  iconBg: '#F37024',
  icon: ZapIcon,
  email: 'lesco@bopay'
}, {
  id: 'p3',
  company: 'SNGPL',
  consumerName: 'Sara Ahmed',
  consumerNumber: '9823498234',
  amount: 3200,
  daysLeft: 2,
  category: 'gas',
  iconBg: '#EF4444',
  icon: FlameIcon,
  email: 'sngpl@bopay'
}];
const ACCOUNTS = [{
  id: 'acc1',
  label: 'Current Account',
  number: '****2656',
  balance: 'Rs 100,000.00'
}, {
  id: 'acc2',
  label: 'Savings Account',
  number: '****8932',
  balance: 'Rs 50,250.00'
}];
const TRANSACTION_FEE = 25;
const OVERDUE_SURCHARGE_PER_DAY = 50;
interface PaymentsScreenProps {
  onBack?: () => void;
  onNavigateHome?: () => void;
}
export function PaymentsScreen({
  onBack,
  onNavigateHome
}: PaymentsScreenProps = {}) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<Step>('home');
  // State
  const [savedPayees, setSavedPayees] = useState<SavedPayee[]>(INITIAL_PAYEES);
  const [homeSearch, setHomeSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('electricity');
  const [billerSearch, setBillerSearch] = useState('');
  const [selectedBiller, setSelectedBiller] = useState<Biller | null>(null);
  const [selectedPayee, setSelectedPayee] = useState<SavedPayee | null>(null);
  const [consumerId, setConsumerId] = useState('');
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [billLoaded, setBillLoaded] = useState(false);
  const [addPayeeToggle, setAddPayeeToggle] = useState(false);
  const [payeeName, setPayeeName] = useState('');
  const [comments, setComments] = useState('');
  const [fromAccount, setFromAccount] = useState(ACCOUNTS[0]);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const [authMethod, setAuthMethod] = useState<AuthMethod>('mpin');
  const [mpin, setMpin] = useState(['', '', '', '']);
  const [authPhase, setAuthPhase] = useState<'idle' | 'scanning' | 'verified'>('idle');
  const [nickname, setNickname] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  // Mock fetched details
  const fetchedBill = {
    consumerName: 'Muhammad Sohail Shawani',
    consumerNumber: consumerId || '03223466549',
    amount: 3448,
    dueDate: '14 May 2026',
    daysLeft: 7
  };
  // Sort payees by days left ascending (most urgent first)
  const sortedPayees = [...savedPayees].sort((a, b) => a.daysLeft - b.daysLeft);
  const homePayees = sortedPayees.slice(0, 3);
  const filteredBillers = ALL_BILLERS.filter(b => b.category === selectedCategory && b.name.toLowerCase().includes(billerSearch.toLowerCase()));
  // Note: bill detail fetching is handled in handleConsumerNext to avoid
  // effect-cleanup race conditions that prevented the loader from completing.
  // Auth effect
  useEffect(() => {
    if (step === 'auth' && authPhase === 'scanning' && (authMethod === 'thumb' || authMethod === 'face')) {
      const timer = setTimeout(() => {
        setAuthPhase('verified');
        setTimeout(() => {
          setStep('processing');
          setTimeout(() => setStep('success'), 1800);
        }, 1000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [step, authPhase, authMethod]);
  // Handlers
  const handleResetFlow = () => {
    setStep('home');
    setSelectedBiller(null);
    setSelectedPayee(null);
    setConsumerId('');
    setBillLoaded(false);
    setLoadingDetails(false);
    setAddPayeeToggle(false);
    setPayeeName('');
    setComments('');
    setBillerSearch('');
    setMpin(['', '', '', '']);
    setAuthPhase('idle');
    setNickname('');
  };
  const handleCategoryClick = (catId: string) => {
    setSelectedCategory(catId);
    setStep('biller_list');
  };
  const handleBillerSelect = (biller: Biller) => {
    setSelectedBiller(biller);
    setStep('enter_consumer');
  };
  const handlePayeeClick = (payee: SavedPayee) => {
    setSelectedPayee(payee);
    setSelectedBiller({
      id: payee.id,
      name: payee.company,
      email: payee.email,
      category: payee.category,
      iconBg: payee.iconBg
    });
    setConsumerId(payee.consumerNumber);
    setBillLoaded(true);
    setPayeeName(payee.consumerName);
    setNickname(payee.consumerName);
    setStep('bill_details'); // Navigate to Payment Details (screen 2)
  };
  const handleConsumerNext = () => {
    if (consumerId.length < 8) return;
    // Inline fetch on the same enter_consumer screen — no step change.
    setBillLoaded(false);
    setLoadingDetails(true);
    const fetchedName = 'Muhammad Sohail Shawani';
    setTimeout(() => {
      setLoadingDetails(false);
      setBillLoaded(true);
      setPayeeName(fetchedName);
    }, 1800);
  };
  const saveCurrentPayee = () => {
    if (!selectedBiller) return;
    const newPayee: SavedPayee = {
      id: `p${Date.now()}`,
      company: selectedBiller.name,
      consumerName: payeeName || fetchedBill.consumerName,
      consumerNumber: consumerId,
      amount: fetchedBill.amount,
      daysLeft: fetchedBill.daysLeft,
      category: selectedBiller.category,
      iconBg: selectedBiller.iconBg,
      icon: getCategoryIcon(selectedBiller.category),
      email: selectedBiller.email
    };
    setSavedPayees(prev => [...prev, newPayee]);
    if (payeeName && payeeName !== fetchedBill.consumerName) {
      setNickname(payeeName);
    }
  };
  const handlePayAction = () => {
    if (addPayeeToggle) {
      saveCurrentPayee();
    }
    setStep('bill_details');
  };
  const handleSaveAction = () => {
    if (!addPayeeToggle) {
      setAddPayeeToggle(true);
      if (!payeeName) {
        setPayeeName(fetchedBill.consumerName);
      }
    } else {
      saveCurrentPayee();
      handleResetFlow();
      if (onNavigateHome) {
        onNavigateHome();
      } else {
        setStep('home');
      }
    }
  };
  const handleJustPayOnly = () => {
    // Pay without saving the payee.
    setStep('review');
  };
  const handleJustPay = () => {
    if (addPayeeToggle) {
      // Save payee
      const newPayee: SavedPayee = {
        id: `p${Date.now()}`,
        company: selectedBiller!.name,
        consumerName: payeeName,
        consumerNumber: consumerId,
        amount: fetchedBill.amount,
        daysLeft: fetchedBill.daysLeft,
        category: selectedBiller!.category,
        iconBg: selectedBiller!.iconBg,
        icon: getCategoryIcon(selectedBiller!.category),
        email: selectedBiller!.email
      };
      setSavedPayees(prev => [...prev, newPayee]);
    }
    setStep('review');
  };
  const handleSavePayee = () => {
    const newPayee: SavedPayee = {
      id: `p${Date.now()}`,
      company: selectedBiller!.name,
      consumerName: payeeName || fetchedBill.consumerName,
      consumerNumber: consumerId,
      amount: fetchedBill.amount,
      daysLeft: fetchedBill.daysLeft,
      category: selectedBiller!.category,
      iconBg: selectedBiller!.iconBg,
      icon: getCategoryIcon(selectedBiller!.category),
      email: selectedBiller!.email
    };
    setSavedPayees(prev => [...prev, newPayee]);
    // Reset transient fields but redirect to the Saved Payees screen per spec 4.3
    setSelectedBiller(null);
    setSelectedPayee(null);
    setConsumerId('');
    setBillLoaded(false);
    setLoadingDetails(false);
    setAddPayeeToggle(false);
    setPayeeName('');
    setComments('');
    setBillerSearch('');
    setMpin(['', '', '', '']);
    setAuthPhase('idle');
    setStep('view_all_payees');
  };
  const handleMpinDigit = (digit: string) => {
    const idx = mpin.findIndex(d => d === '');
    if (idx === -1 || !digit) return;
    const newMpin = [...mpin];
    newMpin[idx] = digit;
    setMpin(newMpin);
    if (idx === 3) {
      setTimeout(() => {
        setStep('processing');
        setTimeout(() => setStep('success'), 1800);
      }, 400);
    }
  };
  const handleMpinDelete = () => {
    const lastFilled = [...mpin].reverse().findIndex(d => d !== '');
    if (lastFilled === -1) return;
    const idx = 3 - lastFilled;
    const newMpin = [...mpin];
    newMpin[idx] = '';
    setMpin(newMpin);
  };
  const getCategoryIcon = (cat: string): React.ElementType => {
    const found = CATEGORIES.find(c => c.id === cat);
    return found?.icon || ZapIcon;
  };
  // Theme styles
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-page-warm';
  const cardBg = isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white';
  const inputBg = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-[#F5EBE6]';
  const textPri = isPlatinum || isDark ? 'text-white' : 'text-[#303030]';
  const textSec = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#818285]';
  const borderClr = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.1)]' : 'border-[#F0E8E3]';
  // ============== HOME STEP ==============
  if (step === 'home') {
    return <motion.div initial="hidden" animate="show" variants={stagger} className={`w-full min-h-screen pb-24 ${bgStyle}`} data-id="element-1651" data-mp="2xmy01pugabx">
        <motion.div variants={fadeUp} className="px-6 pt-6 pb-2 flex items-center justify-between" data-id="element-1652" data-mp="3pdhbhkxbk936">
          <button onClick={onBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} aria-label="Back" data-id="element-1653" data-mp="3kf6199343wkf">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} strokeWidth={2} data-id="element-1654" data-mp="3bg11y0jq7z77" />
          </button>
          <h1 className={`text-lg font-bold ${textPri}`} data-id="element-1655" data-mp="36gy9rgouhngn">Bill Payments</h1>
          <button onClick={onNavigateHome} className="w-10 h-10 rounded-full bg-[#F37024] flex items-center justify-center text-white hover:bg-[#D95A1A] transition-colors shadow-sm" aria-label="Home" data-id="element-1656" data-mp="1lw1b92a7wdm6">
            <HomeIcon className="w-5 h-5" strokeWidth={2.5} data-id="element-1657" data-mp="1zwluu0nyidgq" />
          </button>
        </motion.div>
        <motion.div variants={fadeUp} className="px-6 pb-4" data-id="element-1658" data-mp="136l6u3vew10h">
          <p className={`text-sm ${textSec}`} data-id="element-1659" data-mp="138625vpjvnze">
            Pay bills and manage your payees
          </p>
        </motion.div>

        <div className="px-6 space-y-6" data-id="element-1660" data-mp="1c718d7slgy2p">
          {/* Search Bar */}
          <motion.div variants={fadeUp} data-id="element-1661" data-mp="1c5q3f4nexka0">
            <div className={`flex items-center gap-3 rounded-2xl px-4 py-3.5 ${inputBg} border ${borderClr}`} data-id="element-1662" data-mp="1gxhncwc2crr6">
              <SearchIcon className={`w-5 h-5 ${textSec}`} strokeWidth={2} data-id="element-1663" data-mp="kf25s2ky6x13" />
              <input type="text" value={homeSearch} onChange={e => setHomeSearch(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && homeSearch.trim()) {
                setBillerSearch(homeSearch.trim());
                setStep('biller_list');
              }
            }} placeholder="Search billers, bills or payees" className={`flex-1 outline-none text-sm bg-transparent ${textPri} placeholder:opacity-60`} data-id="element-1664" data-mp="ognbn3z2eb1s" />
              {homeSearch && <button onClick={() => {
              setBillerSearch(homeSearch.trim());
              setStep('biller_list');
            }} className="text-xs font-bold text-[#F37024] uppercase tracking-wider" data-id="element-1665" data-mp="13v6twu8puzpf">
                  Go
                </button>}
            </div>
          </motion.div>

          {/* Categories */}
          <motion.div variants={fadeUp} data-id="element-1666" data-mp="2p6yjstonc1e8">
            <h3 className={`text-[13px] font-bold tracking-wider uppercase mb-4 ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-1667" data-mp="2pa2um9wm9vdv">
              Categories
            </h3>
            <div className="grid grid-cols-3 gap-3" data-id="element-1668" data-mp="2mtlekfvf659p">
              {CATEGORIES.slice(0, 6).map(cat => {
              const Icon = cat.icon;
              return <button key={cat.id} onClick={() => handleCategoryClick(cat.id)} className={`rounded-2xl p-4 flex flex-col items-center gap-3 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-95`} data-id="element-1669" data-mp="3fhqv0qffx83q">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${inputBg}`} data-id="element-1670" data-mp="3aqqxpk2eso0p">
                      <Icon className="w-5 h-5 text-[#F37024]" strokeWidth={2} data-id="element-1671" data-mp="29w7b42ssw3i" />
                    </div>
                    <span className={`text-[11px] font-medium text-center leading-tight ${textPri}`} data-id="element-1672" data-mp="2363r4oiy8fs5">
                      {cat.label}
                    </span>
                  </button>;
            })}
            </div>
          </motion.div>

          {/* Saved Payees */}
          <motion.div variants={fadeUp} data-id="element-1673" data-mp="1pbknyav6l88s">
            <div className="flex items-center justify-between mb-4" data-id="element-1674" data-mp="2m4g3ej55q5ry">
              <h3 className={`text-[13px] font-bold tracking-wider uppercase ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#F37024]' : 'text-[#661806]'}`} data-id="element-1675" data-mp="2m1lh0m2l2avw">
                Saved Payees
              </h3>
              <button onClick={() => setStep('view_all_payees')} className="text-xs font-bold text-[#22C55E] uppercase tracking-wider flex items-center gap-1 hover:opacity-80 transition-opacity" data-id="element-1676" data-mp="284ydu1k1eohj">
                View All
                <ChevronRightIcon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-1677" data-mp="ml2x3tb89vdc" />
              </button>
            </div>

            {/* Payees List */}
            <div className="space-y-3" data-id="element-1678" data-mp="rkxodxyx0myx">
              {homePayees.length === 0 ? <div className={`text-center py-12 rounded-2xl ${cardBg} border ${borderClr}`} data-id="element-1679" data-mp="wi7ms34a88yw">
                  <FileTextIcon className={`w-10 h-10 mx-auto mb-2 ${textSec}`} data-id="element-1680" data-mp="11bacg05xuy9t" />
                  <p className={`text-sm ${textSec}`} data-id="element-1681" data-mp="1aankcoeezesf">No saved payees yet</p>
                </div> : homePayees.map(payee => {
              const Icon = payee.icon;
              const isUrgent = payee.daysLeft <= 3;
              return <button key={payee.id} onClick={() => handlePayeeClick(payee)} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-[0.98]`} data-id="element-1682" data-mp="1k6h68ye0wkmv">
                      <div className="relative shrink-0" data-id="element-1683" data-mp="1y063qhip71g8">
                        <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                    backgroundColor: payee.iconBg
                  }} data-id="element-1684" data-mp="1y0pbx6ijqbh2">
                          <Icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-1685" data-mp="2cok66rc7cnw9" />
                        </div>
                        {isUrgent && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#FBBF24] flex items-center justify-center border-2 border-white" data-id="element-1686" data-mp="2lnxg1l48nehi">
                            <CheckCircle2Icon className="w-3 h-3 text-white" data-id="element-1687" data-mp="rd29eocm69hd" />
                          </div>}
                      </div>
                      <div className="flex-1 text-left min-w-0" data-id="element-1688" data-mp="oraz7tsbpbn4">
                        <h4 className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1689" data-mp="osvvbnuad2n3">
                          {payee.company}
                        </h4>
                        <p className={`text-[12px] truncate ${textSec}`} data-id="element-1690" data-mp="ezot2buue2r8">
                          {payee.consumerName}
                        </p>
                        <p className={`text-[11px] ${textSec}`} data-id="element-1691" data-mp="17qozsukk5kml">
                          {payee.consumerNumber}
                        </p>
                      </div>
                      <div className="text-right shrink-0" data-id="element-1692" data-mp="xs93md6obl68">
                        <p className={`text-[15px] font-bold ${textPri}`} data-id="element-1693" data-mp="xqob0t3x6cb0">
                          Rs. {payee.amount.toLocaleString()}
                        </p>
                        <p className={`text-[11px] font-medium ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-1694" data-mp="3iiqfm5gmhcrb">
                          {payee.daysLeft} days left
                        </p>
                      </div>
                      <ChevronRightIcon className={`w-5 h-5 ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'} shrink-0`} data-id="element-1695" data-mp="3kx54hc4xl3p6" />
                    </button>;
            })}
            </div>
          </motion.div>
        </div>
      </motion.div>;
  }
  // ============== VIEW ALL PAYEES STEP ==============
  if (step === 'view_all_payees') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto`} data-id="element-1696" data-mp="31zc9qx4go02v">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1697" data-mp="2s64umkjx55pl">
          <button onClick={() => setStep('home')} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1698" data-mp="2e9882vau2aei">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1699" data-mp="1zmsofo7wwo03" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1700" data-mp="1unoid6gab6dz">
            All Saved Payees
          </h2>
        </div>

        <div className="px-6 mt-2 mb-4" data-id="element-1701" data-mp="28nh6qv4c9ais">
          <p className={`text-sm ${textSec}`} data-id="element-1702" data-mp="23nkz8lwfn0yh">
            {sortedPayees.length}{' '}
            {sortedPayees.length === 1 ? 'payee' : 'payees'} • Sorted by due
            date
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-1703" data-mp="2dm0r4r3aae91">
          <div className="space-y-3" data-id="element-1704" data-mp="2dlilj3uqenog">
            {sortedPayees.length === 0 ? <div className={`text-center py-12 rounded-2xl ${cardBg} border ${borderClr}`} data-id="element-1705" data-mp="2rbvvtcv3iegy">
                <FileTextIcon className={`w-10 h-10 mx-auto mb-2 ${textSec}`} data-id="element-1706" data-mp="2ndoof8miupif" />
                <p className={`text-sm ${textSec}`} data-id="element-1707" data-mp="31bv190w7xopl">No saved payees yet</p>
              </div> : sortedPayees.map(payee => {
            const Icon = payee.icon;
            const isUrgent = payee.daysLeft <= 3;
            return <button key={payee.id} onClick={() => handlePayeeClick(payee)} className={`w-full rounded-2xl p-4 flex items-center gap-4 shadow-sm hover:shadow-md transition-all border ${cardBg} ${borderClr} active:scale-[0.98]`} data-id="element-1708" data-mp="3kewwjgjvi4py">
                    <div className="relative shrink-0" data-id="element-1709" data-mp="2xgtwzs7ckgqd">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{
                  backgroundColor: payee.iconBg
                }} data-id="element-1710" data-mp="31gub1bqjapww">
                        <Icon className="w-6 h-6 text-white" strokeWidth={2} data-id="element-1711" data-mp="3bgl5xej0b6xi" />
                      </div>
                      {isUrgent && <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-[#FBBF24] flex items-center justify-center border-2 border-white" data-id="element-1712" data-mp="qojel3vbe2z5">
                          <CheckCircle2Icon className="w-3 h-3 text-white" data-id="element-1713" data-mp="2b96bsyjg5j8m" />
                        </div>}
                    </div>
                    <div className="flex-1 text-left min-w-0" data-id="element-1714" data-mp="21g8qkkn2lg6b">
                      <h4 className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1715" data-mp="1wi773ylqfeub">
                        {payee.company}
                      </h4>
                      <p className={`text-[12px] truncate ${textSec}`} data-id="element-1716" data-mp="1nhit33wc9ehf">
                        {payee.consumerName}
                      </p>
                      <p className={`text-[11px] ${textSec}`} data-id="element-1717" data-mp="1ijha0iywxf5y">
                        {payee.consumerNumber}
                      </p>
                    </div>
                    <div className="text-right shrink-0" data-id="element-1718" data-mp="3cwgzr4x0bpqy">
                      <p className={`text-[15px] font-bold ${textPri}`} data-id="element-1719" data-mp="3cvxgnkvmevbc">
                        Rs. {payee.amount.toLocaleString()}
                      </p>
                      <p className={`text-[11px] font-medium ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'}`} data-id="element-1720" data-mp="167orq62nz9if">
                        {payee.daysLeft} days left
                      </p>
                    </div>
                    <ChevronRightIcon className={`w-5 h-5 ${isUrgent ? 'text-[#EF4444]' : 'text-[#22C55E]'} shrink-0`} data-id="element-1721" data-mp="w8651hvdd9mo" />
                  </button>;
          })}
          </div>
        </div>
      </motion.div>;
  }
  // ============== BILLER LIST STEP ==============
  if (step === 'biller_list') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto`} data-id="element-1722" data-mp="1g02savi0aa30">
        {/* Header */}
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1723" data-mp="8l7s2u6gkjti">
          <button onClick={handleResetFlow} className={`p-2 -ml-2 rounded-full ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-1724" data-mp="10dzg7h38olw">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1725" data-mp="az3b3iie5ctp" />
          </button>
          <div className={`flex-1 rounded-full px-4 py-3 flex items-center gap-2 ${inputBg}`} data-id="element-1726" data-mp="ax0gsma7gxj6">
            <SearchIcon className={`w-4 h-4 ${textSec}`} data-id="element-1727" data-mp="3eftl08h3ga0o" />
            <input type="text" placeholder="Search biller" value={billerSearch} onChange={e => setBillerSearch(e.target.value)} className={`flex-1 outline-none text-sm bg-transparent ${textPri} placeholder:opacity-60`} data-id="element-1728" data-mp="3egbq7ulqhykq" />
          </div>
        </div>

        {/* Title */}
        <div className="px-6 mt-2 mb-4" data-id="element-1729" data-mp="2vosjbcola0df">
          <h1 className={`text-3xl font-bold ${textPri}`} data-id="element-1730" data-mp="2vnr6b2o0j3t2">Select Biller</h1>
        </div>

        {/* Category Tabs */}
        <div className="px-6 mb-4 overflow-x-auto" data-id="element-1731" data-mp="2qoeo4v836kwt">
          <div className="flex gap-2 pb-2" data-id="element-1732" data-mp="257xxv5j7mg7s">
            {CATEGORIES.map(cat => <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all ${selectedCategory === cat.id ? 'bg-[#F37024] text-white shadow-md' : `${inputBg} ${textSec}`}`} data-id="element-1733" data-mp="2a57lcfn1ghsz">
                {cat.label}
              </button>)}
          </div>
        </div>

        {/* Biller List */}
        <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-1734" data-mp="2xvlek9hd0mp5">
          {filteredBillers.length === 0 ? <div className={`text-center py-12 ${textSec}`} data-id="element-1735" data-mp="32uevkbvuuvu2">
              No billers found
            </div> : <div className="space-y-1" data-id="element-1736" data-mp="36x1hjw5uhmjc">
              {filteredBillers.map((biller, idx) => <button key={biller.id} onClick={() => handleBillerSelect(biller)} className={`w-full py-4 flex items-center gap-4 ${idx > 0 ? `border-t ${borderClr}` : ''} active:opacity-70 transition-opacity`} data-id="element-1737" data-mp="3bul73s7ztlql" data-mpi={idx}>
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 border ${borderClr} ${cardBg}`} data-id="element-1738" data-mp="r6t5r8f702fk">
                    <span className="font-bold text-sm" style={{
                color: biller.iconBg
              }} data-id="element-1739" data-mp="makxmezex9hw">
                      {biller.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 text-left min-w-0" data-id="element-1740" data-mp="1a3uen4bvamos">
                    <h4 className={`text-[15px] font-bold ${textPri}`} data-id="element-1741" data-mp="156ax9vjvhtsm">
                      {biller.name}
                    </h4>
                    <p className={`text-xs ${textSec}`} data-id="element-1742" data-mp="22ob41se089up">{biller.email}</p>
                  </div>
                </button>)}
            </div>}
        </div>
      </motion.div>;
  }
  // ============== ENTER CONSUMER ID + FETCH + ACTIONS (single screen) ==============
  if (step === 'enter_consumer') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1743" data-mp="1znzoh2ztq7yp">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1744" data-mp="24msqnz9nucjk">
          <button onClick={() => {
          setBillLoaded(false);
          setLoadingDetails(false);
          setConsumerId('');
          setStep('biller_list');
        }} className="p-2 -ml-2" data-id="element-1745" data-mp="24mke68hg998w">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1746" data-mp="2sbvl67pe7zvh" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1747" data-mp="2ndak662bhc5z">
            {billLoaded ? 'Bill Details' : 'Enter Bill ID'}
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1748" data-mp="36zlt069a0lfw">
          {/* Biller Header */}
          <div className="flex flex-col items-center mb-6 mt-2" data-id="element-1749" data-mp="3aze5gu1sjbqj">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mb-4 shadow-lg" style={{
            backgroundColor: selectedBiller?.iconBg
          }} data-id="element-1750" data-mp="2x16a2qkekihh">
              <span className="text-3xl font-bold text-white" data-id="element-1751" data-mp="2na1xfxi799pt">
                {selectedBiller?.name.charAt(0)}
              </span>
            </div>
            <h1 className={`text-2xl font-bold ${textPri}`} data-id="element-1752" data-mp="2s3o3v7xrshd6">
              {selectedBiller?.name}
            </h1>
            <p className={`text-sm ${textSec}`} data-id="element-1753" data-mp="2j99nt35f12t0">{selectedBiller?.email}</p>
          </div>

          {/* Consumer/Bill ID Input */}
          <div className="relative mb-3" data-id="element-1754" data-mp="24da8p8jw22ed">
            <div className={`relative rounded-3xl px-6 py-5 border-2 ${consumerId ? 'border-[#F37024]' : borderClr} ${inputBg} flex items-center gap-3 min-h-[80px] transition-colors`} data-id="element-1755" data-mp="24ebsc1eyzx7p">
              <div className="flex-1 min-w-0 flex flex-col justify-center" data-id="element-1756" data-mp="1lg09o6nud1rf">
                <label className={`text-xs font-bold uppercase tracking-wider transition-all duration-200 ${consumerId ? 'opacity-100 text-[#F37024] mb-1' : 'opacity-0 h-0 overflow-hidden'}`} data-id="element-1757" data-mp="1ldxaaw0stwa1">
                  {selectedBiller?.category === 'credit' ? 'Credit Card Number' : 'Consumer / Bill ID'}
                </label>
                <input type="text" value={consumerId} disabled={loadingDetails || billLoaded} onChange={e => {
                setConsumerId(e.target.value.replace(/[^\d]/g, ''));
                if (billLoaded) setBillLoaded(false);
              }} placeholder={selectedBiller?.category === 'credit' ? 'Enter Credit Card Number' : 'Enter Consumer / Bill ID'} className={`w-full outline-none text-xl tracking-wider bg-transparent font-medium ${textPri} placeholder:opacity-50 placeholder:text-base placeholder:font-normal disabled:opacity-70`} data-id="element-1758" data-mp="2479ngs4nacot" />
              </div>
              {/* Small scan QR icon — scanning routes straight to Payment Details */}
              <button type="button" onClick={() => {
              setConsumerId(selectedBiller?.category === 'credit' ? '4242424242421234' : '03223556978');
              setPayeeName('Muhammad Sohail Shawani');
              setBillLoaded(true);
              setLoadingDetails(false);
              setCustomAmount(fetchedBill.amount.toString());
              setStep('bill_details');
            }} aria-label="Scan QR code" className="shrink-0 w-11 h-11 rounded-full bg-[#F37024]/10 text-[#F37024] flex items-center justify-center hover:bg-[#F37024]/20 active:scale-95 transition-all" data-id="element-1759" data-mp="3e3ptam3pvvlv">
                <QrCodeIcon className="w-6 h-6" strokeWidth={2} data-id="element-1760" data-mp="12x5gmptwxrc" />
              </button>
            </div>
          </div>
          <p className={`text-xs ${textSec} text-center mb-4`} data-id="element-1761" data-mp="3t7rbaquy8l0a">
            {selectedBiller?.category === 'credit' ? 'Enter your card number or tap the scan icon' : 'You can find this on your physical bill, or tap the scan icon'}
          </p>

          {/* Fetch Button — only shown before fetch completes */}
          {!billLoaded && !loadingDetails && <button onClick={handleConsumerNext} disabled={consumerId.length < 8} className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all disabled:opacity-50" data-id="element-1762" data-mp="39he5h75pg1nb">
              FETCH BILL DETAILS
            </button>}

          {/* Inline Loader */}
          <AnimatePresence data-id="element-1763" data-mp="1xp6i55d0izn8">
            {loadingDetails && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} className="flex flex-col items-center justify-center py-8" data-id="element-1764" data-mp="1xqpuwlzgmsk9">
                <motion.div animate={{
              rotate: 360
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'linear'
            }} className="w-12 h-12 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4" data-id="element-1765" data-mp="2ghqth8wo4m3a" />
                <p className={`text-sm font-bold ${textPri}`} data-id="element-1766" data-mp="3coc02wmp6wzt">
                  Fetching bill details...
                </p>
              </motion.div>}
          </AnimatePresence>

          {/* Inline Fetched Details + Actions */}
          <AnimatePresence data-id="element-1767" data-mp="2xxuynda2c72x">
            {billLoaded && !loadingDetails && <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: 'easeOut'
          }} className="mt-2" data-id="element-1768" data-mp="2sy9yizj5psom">
                <div className={`rounded-3xl p-6 ${cardBg} shadow-lg border ${borderClr}`} data-id="element-1769" data-mp="i6741oiiuqna">
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1770" data-mp="d6ulvh2li7r1">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: selectedBiller?.iconBg
                }} data-id="element-1771" data-mp="zf9rqqik1ika">
                      <span className="text-lg font-bold text-white" data-id="element-1772" data-mp="6syf2hhobr74">
                        {selectedBiller?.name.charAt(0)}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1773" data-mp="gqmnubc46z8a">
                      <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1774" data-mp="1n5dr2lopmw5h">
                        {selectedBiller?.name}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-1775" data-mp="2d8itbya133cc">
                    <div className="flex justify-between items-center" data-id="element-1776" data-mp="289fw300ai1ex">
                      <span className={`text-sm ${textSec}`} data-id="element-1777" data-mp="287bkdvw5fy43">
                        Consumer Name
                      </span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-1778" data-mp="2i6u3yb85dhvm">
                        {fetchedBill.consumerName}
                      </span>
                    </div>
                    <div className="flex justify-between items-center" data-id="element-1779" data-mp="2rx5jkv171fic">
                      <span className={`text-sm ${textSec}`} data-id="element-1780" data-mp="2rwvtl7g5f8cn">Consumer ID</span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-1781" data-mp="35vlqon2yx810">
                        {consumerId || fetchedBill.consumerNumber}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Add Payee Toggle */}
                <div className={`mt-6 p-4 rounded-2xl flex items-center justify-between ${cardBg} border ${borderClr}`} data-id="element-1782" data-mp="16km0frf52s6f">
                  <div className="flex-1" data-id="element-1783" data-mp="3i9iefmcrkkqx">
                    <h4 className={`font-bold text-sm ${textPri}`} data-id="element-1784" data-mp="3n6il9zhftrj3">
                      Save as Payee
                    </h4>
                    <p className={`text-xs ${textSec}`} data-id="element-1785" data-mp="38gtenoqksvbf">
                      Quick access for future payments
                    </p>
                  </div>
                  <button onClick={() => setAddPayeeToggle(!addPayeeToggle)} className={`w-12 h-7 rounded-full transition-colors ${addPayeeToggle ? 'bg-[#22C55E]' : isDark || isPlatinum ? 'bg-[#48484A]' : 'bg-[#E5E5EA]'} relative`} data-id="element-1786" data-mp="2zg51ew90ayzl">
                    <motion.div animate={{
                  x: addPayeeToggle ? 22 : 2
                }} transition={{
                  type: 'spring',
                  stiffness: 500,
                  damping: 30
                }} className="absolute top-1 left-0 w-5 h-5 bg-white rounded-full shadow-md" data-id="element-1787" data-mp="2uh1ut7ddokpi" />
                  </button>
                </div>

                {/* Editable Payee Name (when toggle ON) */}
                <AnimatePresence data-id="element-1788" data-mp="2f5t0m62vqxgd">
                  {addPayeeToggle && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="mt-3 overflow-hidden" data-id="element-1789" data-mp="2k4cmy7ldyb99">
                      <div className={`rounded-2xl px-5 py-3 ${inputBg} border ${borderClr}`} data-id="element-1790" data-mp="1mioo7g1gjguk">
                        <label className={`text-[10px] font-bold uppercase tracking-wider ${isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#C4B5AA]' : 'text-[#5A5B5E]'}`} data-id="element-1791" data-mp="1riks7fa5n8a4">
                          Payee Name
                        </label>
                        <input type="text" value={payeeName} onChange={e => setPayeeName(e.target.value)} className={`w-full outline-none text-base bg-transparent font-medium ${textPri}`} data-id="element-1792" data-mp="1hr1g6goyehtx" />
                      </div>
                    </motion.div>}
                </AnimatePresence>

                {/* Action Buttons */}
                <div className="mt-6 flex flex-col gap-3" data-id="element-1793" data-mp="1vlu0rtjpao7d">
                  <button onClick={handleSaveAction} className="w-full border-2 border-[#22C55E] text-[#22C55E] py-4 rounded-full font-bold text-[15px] tracking-wide active:scale-[0.98] transition-all" data-id="element-1794" data-mp="2ahjpw0k6nhgb">
                    SAVE
                  </button>
                  <button onClick={handlePayAction} className="w-full bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all" data-id="element-1795" data-mp="2hk867c200p8u">
                    PAY
                  </button>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </motion.div>;
  }
  // ============== BILL SCREEN (Payment Details) ==============
  if (step === 'bill_details') {
    const isOverdue = fetchedBill.daysLeft < 0;
    const isCreditCard = selectedBiller?.category === 'credit';
    const minPayment = 5000;
    const fullPayment = fetchedBill.amount;
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1796" data-mp="1fv4djvxdy8l5">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1797" data-mp="13jbu3fpehww1">
          <button onClick={() => {
          setShowAccountDropdown(false);
          setStep('enter_consumer');
        }} className="p-2 -ml-2" aria-label="Back" data-id="element-1798" data-mp="yn3hoa2l8hsk">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1799" data-mp="1r8wn6rf9qaj1" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1800" data-mp="1mcfnr12mc9qn">
            Payment Details
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8 space-y-5" data-id="element-1801" data-mp="1hew8pyy3jsxn">
          {/* From Account Card with dropdown */}
          <div data-id="element-1802" data-mp="5xmqtgum98yg">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1803" data-mp="5y5y83mn4ey8">
              From Account
            </p>
            <div className={`rounded-2xl border ${borderClr} ${cardBg} overflow-hidden`} data-id="element-1804" data-mp="10o078a5vk50">
              <button onClick={() => setShowAccountDropdown(!showAccountDropdown)} className="w-full p-4 flex items-center justify-between text-left active:opacity-70 transition-opacity" data-id="element-1805" data-mp="1ujh3r4d2vh1e">
                <div className="flex items-center gap-3 min-w-0 flex-1" data-id="element-1806" data-mp="1kupgh9iypkwt">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#22C55E]/10`} data-id="element-1807" data-mp="28ji50kx98qhe">
                    <BuildingIcon className="w-5 h-5 text-[#22C55E]" strokeWidth={2} data-id="element-1808" data-mp="2dhjvvs0x9u3f" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1809" data-mp="23iud7x17c5m4">
                    <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1810" data-mp="n5vul89ytyp1">
                      {fromAccount.label}
                    </p>
                    <p className={`text-xs truncate ${textSec}`} data-id="element-1811" data-mp="s5ry76er3o42">
                      {fromAccount.number} • {fromAccount.balance}
                    </p>
                  </div>
                </div>
                <ChevronRightIcon className={`w-5 h-5 ${textSec} transition-transform ${showAccountDropdown ? 'rotate-90' : ''}`} strokeWidth={2} data-id="element-1812" data-mp="31cvedfyb4w2d" />
              </button>

              <AnimatePresence data-id="element-1813" data-mp="3bcmblpe95p65">
                {showAccountDropdown && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: 'auto'
              }} exit={{
                opacity: 0,
                height: 0
              }} className="overflow-hidden" data-id="element-1814" data-mp="2nn1p4wiw49s4">
                    <div className={`px-3 pb-3 space-y-2 border-t ${borderClr} pt-3`} data-id="element-1815" data-mp="1zx7d2h6eap8x">
                      {ACCOUNTS.map(acc => <button key={acc.id} onClick={() => {
                    setFromAccount(acc);
                    setShowAccountDropdown(false);
                  }} className={`w-full p-3 rounded-xl text-left flex items-center justify-between transition-colors ${fromAccount.id === acc.id ? 'bg-[#22C55E]/10 border border-[#22C55E]/30' : `${inputBg} border ${borderClr}`}`} data-id="element-1816" data-mp="7v4ortgbp565">
                          <div className="min-w-0" data-id="element-1817" data-mp="3q10tlv6tz6f7">
                            <p className={`text-sm font-bold ${textPri}`} data-id="element-1818" data-mp="3pz6bspquvufc">
                              {acc.label}
                            </p>
                            <p className={`text-xs ${textSec}`} data-id="element-1819" data-mp="15as7qwi0mgn9">
                              {acc.number} • {acc.balance}
                            </p>
                          </div>
                          {fromAccount.id === acc.id && <CheckCircle2Icon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-1820" data-mp="2ur4dvl424q5p" />}
                        </button>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>

          {/* Biller Details Card */}
          <div data-id="element-1821" data-mp="2b3ethyyg9s6i">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1822" data-mp="2b3wut9zyywla">
              Biller Details
            </p>
            <div className={`rounded-2xl border ${borderClr} ${cardBg} overflow-hidden`} data-id="element-1823" data-mp="222ypmodsoj14">
              {/* Header */}
              <div className={`p-4 flex items-center gap-3 border-b ${borderClr}`} data-id="element-1824" data-mp="1x4x4zz0q0bnj">
                <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                backgroundColor: selectedBiller?.iconBg
              }} data-id="element-1825" data-mp="b3jspe7z8qt">
                  <span className="text-lg font-bold text-white" data-id="element-1826" data-mp="35bdujnipy613">
                    {selectedBiller?.name.charAt(0)}
                  </span>
                </div>
                <div className="min-w-0 flex-1" data-id="element-1827" data-mp="2qmheb36idmpe">
                  <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1828" data-mp="2vlc8f19xexot">
                    {selectedBiller?.name}
                  </p>
                  <p className={`text-xs truncate ${textSec}`} data-id="element-1829" data-mp="2hkrkxrt2gdoz">
                    {selectedBiller?.email}
                  </p>
                </div>
              </div>
              {/* Rows */}
              <div className="p-4 space-y-3" data-id="element-1830" data-mp="27ppwyzaj1hnz">
                <div className="flex justify-between items-center" data-id="element-1831" data-mp="2cmq6jk6l52ju">
                  <span className={`text-sm ${textSec}`} data-id="element-1832" data-mp="2cp2lrpkykyt7">Bill Company</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1833" data-mp="5x1p2cmxu61v">
                    {selectedBiller?.name}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1834" data-mp="1vd4ant6pf0m0">
                  <span className={`text-sm ${textSec}`} data-id="element-1835" data-mp="1vbb2uakcuacf">Bill Amount</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1836" data-mp="1qg5gjan1itnt">
                    Rs. {fetchedBill.amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1837" data-mp="20bpavslyhkag">
                  <span className={`text-sm ${textSec}`} data-id="element-1838" data-mp="2e3vhoo37yex8">Consumer Name</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1839" data-mp="2e5zwi12noyca">
                    {payeeName || fetchedBill.consumerName}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1840" data-mp="2o1t6pdswkfqw">
                  <span className={`text-sm ${textSec}`} data-id="element-1841" data-mp="2j2yk00rte71i">Consumer ID</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1842" data-mp="to1wod6nvtux">
                    {'03223556978'}
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-1843" data-mp="oqs0meorojy4">
                  <span className={`text-sm ${textSec}`} data-id="element-1844" data-mp="3tt1txzrtmnbg">Due Date</span>
                  <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1845" data-mp="3pr6wx7gxaw3h">
                    {fetchedBill.dueDate}
                    {isOverdue && ' (Overdue)'}
                  </span>
                </div>
                {isCreditCard && <div className="flex justify-between items-center" data-id="element-1846" data-mp="3b2tubrnbyf00">
                    <span className={`text-sm ${textSec}`} data-id="element-1847" data-mp="3azxxk6n7xwd5">Status</span>
                    <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1848" data-mp="361o2rx5eoxs2">
                      {isOverdue ? 'Unpaid' : 'Pending'}
                    </span>
                  </div>}
              </div>
            </div>
          </div>

          {/* Credit Card Payment Amount options */}
          {isCreditCard && <div data-id="element-1849" data-mp="vbxvpetasyb6">
              <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1850" data-mp="v9boxhe8emu0">
                Payment Amount
              </p>
              <div className={`rounded-2xl border ${borderClr} ${cardBg} p-4 space-y-4`} data-id="element-1851" data-mp="17s797df3fpop">
                <div className="grid grid-cols-2 gap-3" data-id="element-1852" data-mp="1cmuqosbojjts">
                  <button onClick={() => setCustomAmount(minPayment.toString())} className={`p-3 rounded-xl border-2 text-left transition-colors ${customAmount === minPayment.toString() ? 'border-[#F37024] bg-[#F37024]/10' : `${borderClr} ${inputBg}`}`} data-id="element-1853" data-mp="1hia2leq4n5nk">
                    <p className={`text-[11px] ${textSec}`} data-id="element-1854" data-mp="tsft9750nz82">Minimum Due</p>
                    <p className={`text-sm font-bold ${textPri}`} data-id="element-1855" data-mp="xwnhl37kbd5r">
                      Rs. {minPayment.toLocaleString()}
                    </p>
                  </button>
                  <button onClick={() => setCustomAmount(fullPayment.toString())} className={`p-3 rounded-xl border-2 text-left transition-colors ${customAmount === fullPayment.toString() ? 'border-[#F37024] bg-[#F37024]/10' : `${borderClr} ${inputBg}`}`} data-id="element-1856" data-mp="29ahk0hdgim30">
                    <p className={`text-[11px] ${textSec}`} data-id="element-1857" data-mp="j6xtus1zae2y">
                      Total Outstanding
                    </p>
                    <p className={`text-sm font-bold ${textPri}`} data-id="element-1858" data-mp="e84d8qrea6yk">
                      Rs. {fullPayment.toLocaleString()}
                    </p>
                  </button>
                </div>
                <div data-id="element-1859" data-mp="3in43w6oq3tk3">
                  <label className={`text-[11px] font-bold uppercase tracking-widest mb-1.5 block ${textSec}`} data-id="element-1860" data-mp="3ijq2p1rspqe8">
                    Custom Amount
                  </label>
                  <div className={`flex items-center px-4 py-3.5 rounded-xl border ${borderClr} ${inputBg}`} data-id="element-1861" data-mp="3rko9tsxj6e11">
                    <span className={`text-sm font-bold mr-2 ${textPri}`} data-id="element-1862" data-mp="2ywsg8x39d28h">
                      Rs.
                    </span>
                    <input type="text" inputMode="numeric" value={customAmount} onChange={e => setCustomAmount(e.target.value.replace(/[^\d]/g, ''))} placeholder="Enter amount" className={`w-full bg-transparent outline-none text-base font-bold ${textPri} placeholder:opacity-50 placeholder:font-normal`} data-id="element-1863" data-mp="1eehvx1611657" />
                  </div>
                  {customAmount !== '' && Number(customAmount) < minPayment && <p className="text-[11px] text-[#EF4444] mt-1.5" data-id="element-1864" data-mp="1o7oy6d5h0612">
                      Amount must be at least the minimum due (Rs.{' '}
                      {minPayment.toLocaleString()})
                    </p>}
                  {customAmount !== '' && Number(customAmount) > fullPayment && <p className="text-[11px] text-[#EF4444] mt-1.5" data-id="element-1865" data-mp="wzfbh0w5z26t">
                        Amount cannot exceed total outstanding (Rs.{' '}
                        {fullPayment.toLocaleString()})
                      </p>}
                </div>
              </div>
            </div>}

          {/* Comments */}
          <div data-id="element-1866" data-mp="j9ll2e50g9uz">
            <p className={`text-[11px] font-bold uppercase tracking-widest mb-2 ${textSec}`} data-id="element-1867" data-mp="j5xqrcrqrjf7">
              Comments (Optional)
            </p>
            <textarea value={comments} onChange={e => setComments(e.target.value)} placeholder="Add a note for this payment..." rows={3} className={`w-full rounded-2xl px-4 py-3 text-sm outline-none resize-none ${inputBg} ${textPri} placeholder:opacity-50 border ${borderClr}`} data-id="element-1868" data-mp="1bwxvxr1t6t8g" />
          </div>

          {/* Next Button */}
          <div className="mt-auto pt-4" data-id="element-1869" data-mp="3mn687vik4h9e">
            <button disabled={isCreditCard && (customAmount === '' || Number(customAmount) < minPayment || Number(customAmount) > fullPayment)} onClick={() => {
            setShowAccountDropdown(false);
            setStep('review');
          }} className="w-full bg-[#F37024] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1870" data-mp="3ckspe64ndtjt">
              NEXT
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // ============== REVIEW STEP (read-only) ==============
  if (step === 'review') {
    const isOverdue = fetchedBill.daysLeft < 0;
    const overdueSurcharge = isOverdue ? Math.abs(fetchedBill.daysLeft) * OVERDUE_SURCHARGE_PER_DAY : 0;
    const totalAmount = fetchedBill.amount + TRANSACTION_FEE + overdueSurcharge;
    const showNickname = !!nickname && nickname !== fetchedBill.consumerName;
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1871" data-mp="22vsrw5sqqitz">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1872" data-mp="2qry78kkygmpu">
          <button onClick={() => setStep('bill_details')} className="p-2 -ml-2" aria-label="Back" data-id="element-1873" data-mp="2qqdft3txtjw7">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1874" data-mp="30ij3w2h2jh64" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1875" data-mp="2vjgaxged51ei">
            Review Payment
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1876" data-mp="39i63ciq8mcwj">
          {/* Ticket */}
          <div className="relative mt-4" data-id="element-1877" data-mp="35hw0vg1nm4mf">
            <div className={`rounded-3xl ${cardBg} shadow-xl overflow-hidden border ${borderClr}`} data-id="element-1878" data-mp="35ixe9r65737b">
              {/* Top: From Account + Amount (read-only) */}
              <div className="bg-gradient-to-br from-[#F37024] to-[#FB8E4F] p-6 text-white" data-id="element-1879" data-mp="3t3g7qxaorxxp">
                <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-1880" data-mp="3f46o1nn9uloz">
                  From Account
                </p>
                <h3 className="text-lg font-bold" data-id="element-1881" data-mp="3k5w2zedm7jic">{fromAccount.label}</h3>
                <p className="text-xs opacity-80" data-id="element-1882" data-mp="35du6m4ar78n2">
                  {fromAccount.number} • {fromAccount.balance}
                </p>

                <div className="mt-4 pt-4 border-t border-white/20" data-id="element-1883" data-mp="2wegybeyd8q3x">
                  <p className="text-xs font-bold uppercase tracking-widest opacity-80 mb-1" data-id="element-1884" data-mp="2wgbeyh2ltw27">
                    Amount
                  </p>
                  <p className="text-3xl font-bold" data-id="element-1885" data-mp="2mfrcjb3cmlie">
                    Rs. {totalAmount.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Notch separator */}
              <div className="relative h-6" data-id="element-1886" data-mp="36xne18pa16zc">
                <div className={`absolute inset-0 ${cardBg}`} data-id="element-1887" data-mp="36wm08wgvm6dx" />
                <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{
                backgroundColor: isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'
              }} data-id="element-1888" data-mp="31zdkf2j7aefu" />
                <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full" style={{
                backgroundColor: isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'
              }} data-id="element-1889" data-mp="2j82uanlau5p0" />
                <div className="absolute left-6 right-6 top-1/2 border-t-2 border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1890" data-mp="257a0y66qhgnh" />
              </div>

              {/* Body */}
              <div className="p-6 pt-2 space-y-4" data-id="element-1891" data-mp="pmzj2pisrzwf">
                {/* Pay To */}
                <div className="flex items-center gap-3 pb-4 border-b border-dashed" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1892" data-mp="tq5ojuy35n1u">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: selectedBiller?.iconBg
                }} data-id="element-1893" data-mp="ascvdkd9krhn">
                    <span className="text-lg font-bold text-white" data-id="element-1894" data-mp="3ef3ips6qcvj1">
                      {selectedBiller?.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1895" data-mp="2qt8fom93uazp">
                    <p className={`text-[10px] font-bold uppercase tracking-widest ${textSec}`} data-id="element-1896" data-mp="2uw51smxusp8e">
                      Pay To
                    </p>
                    <p className={`text-[15px] font-bold truncate ${textPri}`} data-id="element-1897" data-mp="2zsvf52i9x6q4">
                      {selectedBiller?.name}
                    </p>
                    <p className={`text-xs truncate ${textSec}`} data-id="element-1898" data-mp="277lry2z5o4e7">
                      {selectedBiller?.email}
                    </p>
                  </div>
                </div>

                {/* Detail rows */}
                <div className="space-y-3" data-id="element-1899" data-mp="2gxyc3u325vjt">
                  <div className="flex justify-between items-center" data-id="element-1900" data-mp="1jh9de99en9en">
                    <span className={`text-sm ${textSec}`} data-id="element-1901" data-mp="1jewxe1n7j948">Bill Company</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1902" data-mp="1oehx4ea7bli0">
                      {selectedBiller?.name}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1903" data-mp="1bwypu2ol683n">
                    <span className={`text-sm ${textSec}`} data-id="element-1904" data-mp="16ydqs6l2lugs">Bill Amount</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1905" data-mp="16wkm2wu0pkbf">
                      Rs. {fetchedBill.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1906" data-mp="xza5qx2cf4zn">
                    <span className={`text-sm ${textSec}`} data-id="element-1907" data-mp="t6pdw12zz1yr">Consumer Name</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1908" data-mp="o7mjrbok2g5k">
                      {fetchedBill.consumerName}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1909" data-mp="j6q98f9p7vr6">
                    <span className={`text-sm ${textSec}`} data-id="element-1910" data-mp="f5wm3o42ykzc">Consumer ID</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1911" data-mp="a7v1gyr0adlr">
                      {consumerId}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1912" data-mp="mvhaor0f099">
                    <span className={`text-sm ${textSec}`} data-id="element-1913" data-mp="4nnozdk8gx3f">Due Date</span>
                    <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-1914" data-mp="3msiid9ptc9u8">
                      {fetchedBill.dueDate}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-1915" data-mp="jbs8gjr41cn9">
                    <span className={`text-sm ${textSec}`} data-id="element-1916" data-mp="o8s5k55zf528">
                      Transaction Fee
                    </span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1917" data-mp="9kxeet7mh8r9">
                      Rs. {TRANSACTION_FEE.toLocaleString()}
                    </span>
                  </div>

                  {/* Overdue surcharge (conditional) */}
                  {isOverdue && <div className="flex justify-between items-center bg-[#EF4444]/10 -mx-2 px-2 py-2 rounded-lg" data-id="element-1918" data-mp="171cwvi61923x">
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1919" data-mp="2rp5j6s2snk94">
                        Overdue Surcharge
                      </span>
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-1920" data-mp="30j22lz4kqiku">
                        Rs. {overdueSurcharge.toLocaleString()}
                      </span>
                    </div>}

                  {/* Total */}
                  <div className={`flex justify-between items-center pt-3 border-t ${borderClr}`} data-id="element-1921" data-mp="28snuykn5m2hm">
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1922" data-mp="2hssqnxizjcfy">
                      Total Amount
                    </span>
                    <span className="text-base font-bold text-[#22C55E]" data-id="element-1923" data-mp="2ctg3tcs465dd">
                      Rs. {totalAmount.toLocaleString()}
                    </span>
                  </div>
                </div>

                {/* Nickname (conditional) */}
                {showNickname && <div className={`pt-4 border-t ${borderClr}`} data-id="element-1924" data-mp="ne6quankzqkc">
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-1925" data-mp="nfrjtvu8z1g3">
                      Nickname
                    </p>
                    <p className={`text-sm font-bold ${textPri}`} data-id="element-1926" data-mp="xe7eu9weauuv">{nickname}</p>
                  </div>}

                {/* Comments display (conditional, read-only) */}
                {comments.trim() && <div className={`pt-4 border-t ${borderClr}`} data-id="element-1927" data-mp="1bbcdvpxoytgm">
                    <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-1928" data-mp="1b9j6g8f9857k">
                      Comments
                    </p>
                    <p className={`text-sm ${textPri} leading-relaxed`} data-id="element-1929" data-mp="1l3i52xg4d6ur">
                      {comments}
                    </p>
                  </div>}
              </div>
            </div>
          </div>

          <div className="mt-auto pt-6" data-id="element-1930" data-mp="3ek5zr9xna0ty">
            <button onClick={() => {
            setMpin(['', '', '', '']);
            setAuthPhase('idle');
            setAuthMethod('mpin');
            setStep('processing');
            setTimeout(() => setStep('success'), 1800);
          }} className="w-full bg-[#F37024] text-white py-4 rounded-full font-bold text-[15px] tracking-wide shadow-[0_8px_20px_rgba(34,197,94,0.3)] active:scale-[0.98] transition-all" data-id="element-1931" data-mp="3ekxoc0stqyaq">
              PAY BILL
            </button>
          </div>
        </div>
      </motion.div>;
  }
  // ============== AUTH STEP ==============
  if (step === 'auth') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1932" data-mp="3jjis2a7ac03x">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1933" data-mp="g0fo48pjv23j">
          <button onClick={() => setStep('review')} className="p-2 -ml-2" data-id="element-1934" data-mp="2abnqyarrwn5f">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1935" data-mp="2ec7p9ho31wt5" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1936" data-mp="20kt4p6ej1njz">
            {authPhase === 'scanning' ? 'Authenticating' : 'Enter MPIN'}
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1937" data-mp="25hjkdsmf6h4v">
          {authPhase === 'scanning' ? <div className="flex flex-col items-center justify-center h-full" data-id="element-1938" data-mp="1qohrme744toh">
              <div className="w-16 h-16 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4 animate-spin" style={{
            borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
          }} data-id="element-1939" data-mp="1vn1fik59ofjh" />
              <p className={`text-sm font-bold ${textPri}`} data-id="element-1940" data-mp="3gz2um4yf3kcx">
                {authPhase === 'scanning' ? 'Authenticating...' : 'Enter MPIN'}
              </p>
            </div> : <div className="space-y-4" data-id="element-1941" data-mp="3m46lo81gtkbi">
              <div className="flex flex-col items-center" data-id="element-1942" data-mp="3m57zulds2mxg">
                <p className={`text-sm ${textSec} mb-2`} data-id="element-1943" data-mp="3q77jnsd1ove">
                  Enter your 4-digit MPIN
                </p>
                <div className="flex gap-2" data-id="element-1944" data-mp="3v3a3z9st5voy">
                  {mpin.map((digit, idx) => <input key={idx} type="text" value={digit} onChange={e => handleMpinDigit(e.target.value)} className={`w-12 h-12 rounded-2xl text-center font-bold text-lg ${textPri} border ${borderClr} outline-none`} maxLength={1} autoFocus={idx === 0} data-id="element-1945" data-mp="dmsbs6wk2vy6" data-mpi={idx} />)}
                </div>
                <button onClick={handleMpinDelete} className="mt-4 w-12 h-12 rounded-2xl flex items-center justify-center text-[#6B7280] hover:bg-[#1F2937] transition-colors" data-id="element-1946" data-mp="2ket3t6jmt0j6">
                  <XIcon className="w-5 h-5" strokeWidth={2} data-id="element-1947" data-mp="3dx19xknrx4lp" />
                </button>
              </div>

              <div className="flex flex-col items-center" data-id="element-1948" data-mp="3iqc9r1u64l44">
                <p className={`text-sm ${textSec} mb-2`} data-id="element-1949" data-mp="3mr5uxng87ltb">
                  {authMethod === 'thumb' ? 'Place your thumb' : 'Place your face'}
                </p>
                <div className={`w-32 h-32 rounded-full flex items-center justify-center border-2 ${borderClr} ${cardBg} ${authPhase === 'scanning' ? 'animate-pulse' : ''}`} data-id="element-1950" data-mp="2uyyd4jozi3dc">
                  {authPhase === 'scanning' ? <div className="w-24 h-24 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] animate-spin" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1951" data-mp="33y1zbwjg6yw2" /> : <div className="w-24 h-24 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024]" style={{
                borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
              }} data-id="element-1952" data-mp="jn9gp50oswb9" />}
                </div>
              </div>
            </div>}
        </div>
      </motion.div>;
  }
  // ============== PROCESSING STEP ==============
  if (step === 'processing') {
    return <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1953" data-mp="1v2oda7ozhm5u">
        <div className="px-6 pt-6 pb-3 flex items-center gap-3" data-id="element-1954" data-mp="2cd8j2rb3d5c1">
          <button onClick={() => setStep('review')} className="p-2 -ml-2" data-id="element-1955" data-mp="2q3u3wlk3vpck">
            <ArrowLeftIcon className={`w-6 h-6 ${textPri}`} data-id="element-1956" data-mp="2q5y6n069qib9" />
          </button>
          <h2 className={`text-lg font-bold flex-1 ${textPri}`} data-id="element-1957" data-mp="2l5ahyl5iidk7">
            Processing Payment
          </h2>
        </div>

        <div className="px-6 flex-1 flex flex-col pb-8" data-id="element-1958" data-mp="1xh93ywpke4xe">
          <div className="flex flex-col items-center justify-center h-full" data-id="element-1959" data-mp="1temh7a7r3a72">
            <div className="w-16 h-16 rounded-full border-4 border-[#F37024]/20 border-t-[#F37024] mb-4 animate-spin" style={{
            borderColor: isPlatinum ? '#48484A' : isDark ? '#3D2518' : '#E0D5CC'
          }} data-id="element-1960" data-mp="27dvmvfybylws" />
            <p className={`text-sm font-bold ${textPri}`} data-id="element-1961" data-mp="3dt6d3lffnxev">
              Processing payment...
            </p>
          </div>
        </div>
      </motion.div>;
  }
  // ============== RECEIPT STEP ==============
  if (step === 'success') {
    const isOverdue = fetchedBill.daysLeft < 0;
    const overdueSurcharge = isOverdue ? Math.abs(fetchedBill.daysLeft) * OVERDUE_SURCHARGE_PER_DAY : 0;
    const totalAmount = fetchedBill.amount + TRANSACTION_FEE + overdueSurcharge;
    const showNickname = !!nickname && nickname !== fetchedBill.consumerName;
    const txnId = `TXN${Date.now().toString().slice(-9)}`;
    const dateTime = new Date().toLocaleString('en-PK', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
    return <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className={`fixed inset-0 z-50 flex flex-col ${bgStyle} max-w-[430px] mx-auto overflow-y-auto`} data-id="element-1962" data-mp="1eie7nxftj78w">
        {/* Green success banner */}
        <div className="bg-gradient-to-br from-[#F37024] to-[#16A34A] px-6 pt-12 pb-12 relative" data-id="element-1963" data-mp="2gv7b67r67m6t">
          <button onClick={handleResetFlow} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" aria-label="Close" data-id="element-1964" data-mp="2bw4h1icqb0dm">
            <XIcon className="w-5 h-5 text-white" strokeWidth={2.5} data-id="element-1965" data-mp="21yq0zjupwdlu" />
          </button>
          <div className="flex flex-col items-center text-white" data-id="element-1966" data-mp="1xwv1mkwckaap">
            <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            damping: 12,
            stiffness: 200,
            delay: 0.15
          }} className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mb-4" data-id="element-1967" data-mp="1xy648he23c08">
              <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={2} data-id="element-1968" data-mp="1a6ij6g3h6v5v" />
            </motion.div>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80" data-id="element-1969" data-mp="3lw6upre9cvja">
              Payment Successful
            </p>
            <p className="text-4xl font-bold mt-2" data-id="element-1970" data-mp="3uv24fdgignrc">
              Rs. {totalAmount.toLocaleString()}
            </p>
            <p className="text-sm opacity-80 mt-1" data-id="element-1971" data-mp="3or8fvqcyd6v">
              Paid to {selectedBiller?.name}
            </p>
          </div>
        </div>

        {/* Wavy separator */}
        <svg viewBox="0 0 430 30" className="-mt-px" preserveAspectRatio="none" style={{
        height: 30
      }} data-id="element-1972" data-mp="374ybc00y86el">
          <path d="M0,0 C120,30 280,30 430,0 L430,30 L0,30 Z" fill={isPlatinum ? '#1C1C1E' : isDark ? '#1A0E08' : '#FFF7F3'} data-id="element-1973" data-mp="3c27zzdgike1d" />
        </svg>

        {/* Receipt body */}
        <div className="px-6 -mt-4 pb-8" data-id="element-1974" data-mp="2jdsubhj9v82p">
          <div className={`rounded-3xl ${cardBg} shadow-xl border ${borderClr} overflow-hidden relative`} data-id="element-1975" data-mp="2odyiuou8wegk">
            {/* PAID watermark */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none opacity-[0.07] z-0" data-id="element-1976" data-mp="pwwobhl9vguf">
              <p className="text-[100px] font-black text-[#22C55E] tracking-tighter rotate-[-20deg]" data-id="element-1977" data-mp="13okuz5cr5d40">
                PAID
              </p>
            </div>

            <div className="p-6 relative z-10" data-id="element-1978" data-mp="yvrrslwxmcu1">
              <h3 className={`text-xs font-bold uppercase tracking-widest mb-4 ${textSec}`} data-id="element-1979" data-mp="1dlfwzy88hvn1">
                Transaction Details
              </h3>
              <div className="space-y-3 mb-5" data-id="element-1980" data-mp="1mnp3md0dt4v5">
                <div className="flex justify-between" data-id="element-1981" data-mp="1mmdzud6xrx41">
                  <span className={`text-sm ${textSec}`} data-id="element-1982" data-mp="1hnucq9glwfa3">Transaction ID</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1983" data-mp="3oeabdmt3t6yt">
                    {txnId}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-1984" data-mp="3jgqwckol0q5t">
                  <span className={`text-sm ${textSec}`} data-id="element-1985" data-mp="1yxoidjhbd6ew">Date & Time</span>
                  <span className={`text-sm font-bold ${textPri}`} data-id="element-1986" data-mp="1yzhpezvu9snf">
                    {dateTime}
                  </span>
                </div>
              </div>

              <div className={`pt-4 border-t border-dashed ${isPlatinum ? 'border-[#48484A]' : isDark ? 'border-[#3D2518]' : 'border-[#E0D5CC]'}`} data-id="element-1987" data-mp="1q4bghs19uugo">
                <h3 className={`text-xs font-bold uppercase tracking-widest mb-3 ${textSec}`} data-id="element-1988" data-mp="1g6nkkyrxjgwx">
                  Bill Details
                </h3>
                <div className="space-y-3" data-id="element-1989" data-mp="176iecryh2okc">
                  <div className="flex justify-between" data-id="element-1990" data-mp="177tgyog6lq9v">
                    <span className={`text-sm ${textSec}`} data-id="element-1991" data-mp="1b7an7k3fmrg3">Bill Company</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1992" data-mp="31k9wygys4irx">
                      {selectedBiller?.name}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1993" data-mp="35lbnm9emrmia">
                    <span className={`text-sm ${textSec}`} data-id="element-1994" data-mp="idxgkwtrkzyt">Bill Amount</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1995" data-mp="deb2kl643foy">
                      Rs. {fetchedBill.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1996" data-mp="9etx3rqoqijs">
                    <span className={`text-sm ${textSec}`} data-id="element-1997" data-mp="4eo7eh3z764c">Consumer Name</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-1998" data-mp="114f7cvv1dlhj">
                      {fetchedBill.consumerName}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-1999" data-mp="x4o96736k32z">
                    <span className={`text-sm ${textSec}`} data-id="element-2000" data-mp="saagwdjtec2j">Consumer ID</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-2001" data-mp="ndaag1j1z7aw">
                      {consumerId}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2002" data-mp="1ktpjy0zhjqbf">
                    <span className={`text-sm ${textSec}`} data-id="element-2003" data-mp="1g0e9lpvgsrer">Due Date</span>
                    <span className={`text-sm font-bold ${isOverdue ? 'text-[#EF4444]' : textPri}`} data-id="element-2004" data-mp="1ncu9pvbsee61">
                      {fetchedBill.dueDate}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2005" data-mp="1x8xd14idkiuk">
                    <span className={`text-sm ${textSec}`} data-id="element-2006" data-mp="21clk7m33hcfa">From Account</span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-2007" data-mp="26b6lzpxzw45u">
                      {fromAccount.label} {fromAccount.number}
                    </span>
                  </div>
                  <div className="flex justify-between" data-id="element-2008" data-mp="2b86qvxj3z0vd">
                    <span className={`text-sm ${textSec}`} data-id="element-2009" data-mp="2g21a6zusxdtx">
                      Transaction Fee
                    </span>
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-2010" data-mp="2kz9sr1jv3jvp">
                      Rs. {TRANSACTION_FEE.toLocaleString()}
                    </span>
                  </div>
                  {isOverdue && <div className="flex justify-between" data-id="element-2011" data-mp="2tyoe5ml6949c">
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-2012" data-mp="3faelk9mxis5u">
                        Overdue Surcharge
                      </span>
                      <span className="text-sm font-bold text-[#EF4444]" data-id="element-2013" data-mp="3aez7phox8w9f">
                        Rs. {overdueSurcharge.toLocaleString()}
                      </span>
                    </div>}
                  <div className={`flex justify-between pt-3 border-t ${borderClr}`} data-id="element-2014" data-mp="2wl822keb9wiz">
                    <span className={`text-sm font-bold ${textPri}`} data-id="element-2015" data-mp="2rmwqa44ghby4">
                      Total Paid
                    </span>
                    <span className="text-base font-bold text-[#22C55E]" data-id="element-2016" data-mp="36ckqf20wi4k9">
                      Rs. {totalAmount.toLocaleString()}
                    </span>
                  </div>
                  {showNickname && <div className="flex justify-between" data-id="element-2017" data-mp="2dsm6803em5ye">
                      <span className={`text-sm ${textSec}`} data-id="element-2018" data-mp="28uklzbu8s0lc">Nickname</span>
                      <span className={`text-sm font-bold ${textPri}`} data-id="element-2019" data-mp="od33q3l0y5tv">
                        {nickname}
                      </span>
                    </div>}
                  {comments.trim() && <div className={`pt-3 border-t ${borderClr}`} data-id="element-2020" data-mp="eggobubgw6z8">
                      <p className={`text-[10px] font-bold uppercase tracking-widest mb-1 ${textSec}`} data-id="element-2021" data-mp="je8mh62pgpbv">
                        Comments
                      </p>
                      <p className={`text-sm ${textPri} leading-relaxed`} data-id="element-2022" data-mp="1c3pa7qfdri1r">
                        {comments}
                      </p>
                    </div>}
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3 mt-6" data-id="element-2023" data-mp="1272ufg1wvh6l">
            <button className="flex-1 py-4 rounded-full font-bold text-sm tracking-wide flex items-center justify-center gap-2 border-2 border-[#22C55E] text-[#22C55E] active:scale-[0.98] transition-all" data-id="element-2024" data-mp="3it3vutraxd64">
              <ShareIcon className="w-4 h-4" data-id="element-2025" data-mp="109gafxxy75ou" />
              SHARE
            </button>
            <button onClick={handleResetFlow} className="flex-1 bg-gradient-to-r from-[#22C55E] to-[#16A34A] text-white py-4 rounded-full font-bold text-sm tracking-wide active:scale-[0.98] transition-all" data-id="element-2026" data-mp="14bt59rb5pj6u">
              DASHBOARD
            </button>
          </div>
        </div>
      </motion.div>;
  }
  return null;
}